import { ReactElement } from 'react';
import { notification } from 'antd';
import { PaginatedParams } from 'core/http/pagination';
import {
    ICS2ConsignmentHouseLevel,
    ICS2EnsDeclaration,
    ICS2EnsDeclarationPayload,
} from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { FormikErrors, setNestedObjectValues, useFormik } from 'formik';
import useDeclarationAutoRefresh from 'hooks/useDeclarationAutoRefresh';
import useDeclarationFormErrors from 'hooks/useDeclarationFormErrors';
import useDeclarationNotifications from 'hooks/useDeclarationNotifications';
import useDeclarations, { UpdateDeclarationFunction } from 'hooks/useDeclarations';
import useDeclarationValidation from 'hooks/useDeclarationValidation';
import useExchanges from 'hooks/useExchanges';
import useProducts, { CreateProductFunction, ListProductFunction, UpdateProductFunction } from 'hooks/useProducts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Declaration } from 'store/declarations/declaration';
import {
    FormAction,
    removeEmptyObjects,
    removeEmptyObjectsFromDeclarationArrays,
    trimWhiteSpaces,
} from 'views/declarations/utils/form-utils';
import { get, isEmpty, isEqual, merge, omit, set } from 'lodash';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { AnyObjectSchema } from 'yup';
import useHandleFormAction from './utils/useHandleFormAction';
import useAutoSaveProduct from './utils/useAutoSaveProduct';
import attachItemIds from './utils/attachItemIds';
import useHandleErrors from './utils/useHandleErrors';
import listItemsIntoForm, { ItemPathMappings } from './utils/listItemsIntoForm';
import useHandleUnsavedChanges from './utils/useHandleUnsavedChanges';
import validate, { FormModel, transformErrorsForFormik } from 'views/declarations/uk/export/validations/validations';
import { TemplateContextProvider } from 'components/ui/composed/template/TemplateContext';
import useUpdateViewOfDeclaration from './utils/useUpdateViewOfDeclaration';
import DeclarationSourceContextProvider from '../../../../components/ui/composed/declarations/source/DeclarationSourceContext';
import { IrelandExportDeclaration } from '../../../../store/declarations/ireland/export-declaration';
import useFormUtils from '../../../../hooks/useFormUtils';
import {
    irelandExportRepresentativeContactPersonLevelRequired,
    irelandExportRepresentativeEoriLevelRequired,
} from '../../ireland/export/validation/IrelandExportB1Validation';
import { deepObjHasTruthyNonObjValue } from '../../../../core/utils/objects';
import useGetDeclarationMapValues from '../../../../hooks/useGetDeclarationMapValues';
import { DeclarationContextProvider } from '../../common/DeclarationContext';
import useJobs from '../../../../hooks/useJobs';
import GvmsHeaderDraftMessage from '../../uk/gvms/components/GvmsHeaderDraftMessage';
import { generateDucr } from '../../../../store/declarations/client';
import useCDSPreviousDocumentUtils from '../../../../hooks/useCdsPreviousDocumentUtils';
import { CdsDeclarationPayloadPayload } from '../../../../store/declarations/uk/cds-declaration';
import { DeclarationInternalType } from '../../../../store/declarations/enums/common/declaration-internal-type';
import { DeclarationExternalEntity } from '../../../../store/declarations/enums/common/declaration-external-entity';
import DeclarationPopupManager from './components/DeclarationPopupManager';
import { NctsDeclaration } from '../../../../store/declarations/ireland/ncts-declaration';
import { Events } from 'utils/events.enum';
import { getDeclarationUrl } from 'views/declarations/utils/getDeclarationUrl';
import Notification from 'components/ui/base/notification/Notification';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DeclarationFieldName } from 'views/declarations/common/declarationType';
import ProductContextProvider from './context/ProductContext';
import { StyledHeader, StyledLayout } from './Form.styles';
import { ProductsTypes } from 'store/products/reducer';
import { DeclarationStatuses } from 'store/declarations/enums/common/declaration-status';
import PostMirrorCompletionPrompt from 'views/declarations/mirroring/post-mirror-completion/PostMirrorCompletionPrompt';
import usePostMirrorCompletionRequest from 'views/declarations/mirroring/post-mirror-completion/usePostMirrorCompletionRequest';

export type Declarations = NonNullable<Declaration[DeclarationFieldName]>;

interface Transform<TReturn = any> {
    forClient?: (data: any, straight?: boolean) => TReturn;
    forServer?: (data: any, straight?: boolean) => TReturn;
}

export enum FormTabs {
    MASTER_DETAILS = 'master-details',
    MASTER_ITEMS = 'master-items',
    HOUSE_CONSIGNMENTS = 'house-consignments',
    PRODUCTS = 'products',
}

export interface TransformData {
    product?: Transform;
    declaration?: Transform;
    houseConsignment?: Transform;
    houseGoodsItem?: Transform;
}

const DeclarationFormTab = <TDeclaration extends Declarations>(): ReactElement => {
    const { declarationId, productId: urlParamsProductId, manifestId, jobId, consignmentId } = useParams();
    const [declaration, setDeclaration] = useState<Declaration | undefined>(undefined);
    const [triedToSubmit, setTriedToSubmit] = useState<boolean>(false);
    const [cancelClicked, setCancelClicked] = useState<boolean>(false);
    const [productId, setProductId] = useState<string | undefined>(urlParamsProductId);
    const [declarationSaved, setDeclarationSaved] = useState(true);

    const declarations = useDeclarations();
    const { job } = useJobs({ jobId: declarations?.declaration?.jobId });
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const products = useProducts({ productId });
    const { setFormDeclarationErrors, clearFormDeclarationErrors, hasFormDeclarationErrors } =
        useDeclarationFormErrors();
    const { listExchanges } = useExchanges();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { showErrorNotification } = useDeclarationNotifications();
    const { setFormAction, setHasUnsavedChanges, isSavingAsDraft } = useDeclarationValidation({
        formAction: null,
    });

    const { isAes } = useFormUtils();
    const { ...mapper } = useGetDeclarationMapValues();

    const baseUrl = getDeclarationUrl({
        manifestId,
        jobId,
        declarationId,
    });

    const location = useLocation();
    const inViewOnly = location.pathname.split('/').includes('view-only');

    const currentDeclaration = useMemo(
        () => declaration?.[mapper.declarationType],
        [declaration, mapper.declarationType]
    );

    const getValidationSchema = (): AnyObjectSchema | undefined =>
        mapper.declarationValidationSchema instanceof Function
            ? mapper.declarationValidationSchema(products.products?.total)
            : mapper.declarationValidationSchema;

    const getDeclarationFormValidations = useCallback(
        (formikValues: TDeclaration) => {
            if (!isAes) return mapper.declarationFormValidations;

            if (deepObjHasTruthyNonObjValue((formikValues as IrelandExportDeclaration)?.representative?.contactPerson))
                return merge(
                    {},
                    mapper.declarationFormValidations,
                    irelandExportRepresentativeContactPersonLevelRequired
                );
            else if (deepObjHasTruthyNonObjValue((formikValues as IrelandExportDeclaration)?.representative))
                return merge({}, mapper.declarationFormValidations, irelandExportRepresentativeEoriLevelRequired);
            else return mapper.declarationFormValidations;
        },
        [mapper.declarationFormValidations, isAes]
    );

    const masterFormik = useFormik<TDeclaration>({
        initialValues:
            (mapper.payloadPath ? get(currentDeclaration, mapper.payloadPath) : (currentDeclaration as any)) ?? {},
        enableReinitialize: true,
        validateOnMount: true,
        validateOnChange: false,
        validationSchema: getValidationSchema(),
        validate: mapper.declarationFormValidations
            ? async (values) => {
                  // TODO: Memoize already validated fields to speed up the process.
                  // If we don't do this, we may end up with a lot of unnecessary requests to eori check service for example.
                  return transformErrorsForFormik(
                      await validate(new FormModel(values), getDeclarationFormValidations(values))
                  );
              }
            : () => {},
        onSubmit: () => {},
    });

    const productInitialValues = useMemo(() => {
        if (mapper.transformData?.product?.forClient) {
            return mapper.transformData?.product?.forClient(
                isEmpty(products.product)
                    ? declarations.declarationTemplate?.template?.product.defaults
                    : products.product
            );
        }
        if (isEmpty(products.product)) {
            return declarations.declarationTemplate?.template?.product.defaults;
        }
        return products.product;
    }, [declarations.declarationTemplate?.template?.product.defaults, products.product, mapper.transformData?.product]);

    const productsFormik = useFormik({
        initialValues: productInitialValues ?? {},
        enableReinitialize: true,
        validateOnMount: true,
        validateOnChange: false,
        validationSchema: mapper.productValidationSchema,
        validate: mapper.productFormValidations
            ? async (values) =>
                  // TODO: Memoize already validated fields to speed up the process.
                  // If we don't do this, we may end up with a lot of unnecessary requests to eori check service for example.
                  transformErrorsForFormik(await validate(new FormModel(values), mapper.productFormValidations!))
            : () => {},
        onSubmit: () => {},
    });

    // ICS2 house consignments live in the declaration payload rather than behind an endpoint, so the
    // one being edited is looked up in the master level by the id in the url.
    const houseConsignments = useMemo(
        () => (currentDeclaration as ICS2EnsDeclaration)?.payload?.consignmentMasterLevel?.consignmentHouseLevel ?? [],
        [currentDeclaration]
    );

    const houseConsignmentInitialValues = useMemo(() => {
        if (!consignmentId) return {};

        return houseConsignments.find(({ id }) => id === consignmentId) ?? {};
    }, [houseConsignments, consignmentId]);

    // Position in the declaration, counted from 1 - the way the parser numbers the consignments.
    const houseConsignmentIndex = useMemo(
        () => (consignmentId ? houseConsignments.findIndex(({ id }) => id === consignmentId) + 1 : 0),
        [houseConsignments, consignmentId]
    );

    const houseConsignmentFormik = useFormik<ICS2ConsignmentHouseLevel>({
        initialValues: houseConsignmentInitialValues,
        enableReinitialize: true,
        validateOnMount: true,
        validateOnChange: false,
        validate: mapper.houseConsignmentFormValidations
            ? async (values) =>
                  transformErrorsForFormik(
                      await validate(new FormModel(values), mapper.houseConsignmentFormValidations!)
                  )
            : () => {},
        onSubmit: () => {},
    });

    const touchFieldsToShowErrorsMasterDetails = useCallback(
        (validations: FormikErrors<TDeclaration>) => {
            masterFormik.setTouched(setNestedObjectValues(validations, true));
        },
        [masterFormik]
    );

    // Runs the master form validation and marks whatever failed as touched, so the offending
    // fields are highlighted in the form. Returns the errors so callers can decide what to do
    // with them - which of them to report, and whether an error should stop the flow at all.
    const runMasterFormValidation = useCallback(
        async (values?: TDeclaration) => {
            const masterDetailsValidation = await masterFormik.validateForm(values);

            touchFieldsToShowErrorsMasterDetails(masterDetailsValidation);

            if (isEmpty(masterDetailsValidation)) {
                // Reset errors
                clearFormDeclarationErrors();
            }

            return masterDetailsValidation;
        },
        [masterFormik, touchFieldsToShowErrorsMasterDetails, clearFormDeclarationErrors]
    );

    const validateForm = useCallback(
        async (values?: TDeclaration) => {
            const masterDetailsValidation = await runMasterFormValidation(values);

            if (isEmpty(masterDetailsValidation)) return true;

            if (isSavingAsDraft) return true;

            if (mapper.parseForm) {
                const parsedForm = mapper.parseForm(masterDetailsValidation);
                const validatedValues = values ?? masterFormik.values;
                const itemsWithIds =
                    mapper.getItemsWithIds?.(declarations.declaration) ?? get(validatedValues, mapper.itemPath ?? '');

                setFormDeclarationErrors({
                    ...parsedForm,
                    items: attachItemIds(parsedForm.items, itemsWithIds, mapper.itemPath),
                });
            }

            return false;
        },
        [
            mapper,
            masterFormik.values,
            runMasterFormValidation,
            isSavingAsDraft,
            setFormDeclarationErrors,
            declarations.declaration,
        ]
    );

    // Master level validation for declaration mirroring.
    // Item errors are omitted since we validate them in the mirroring preparation page.
    // A silent run only reports whether the master level passes - it leaves the form and the
    // sidebar untouched, so the mirroring flow can decide whether the user needs to be asked
    // about validating at all before showing them any errors.
    const validateMasterLevelForm = useCallback(
        async ({ silent = false }: { silent?: boolean } = {}) => {
            const masterDetailsValidation = silent
                ? await masterFormik.validateForm()
                : await runMasterFormValidation();

            const masterLevelErrors = (mapper.itemPath
                ? omit(masterDetailsValidation, mapper.itemPath)
                : masterDetailsValidation) as unknown as FormikErrors<TDeclaration>;

            // `omit` leaves the item path's parent behind as an empty object (e.g. `goodsShipment: {}`),
            // so emptiness has to be checked by looking for a remaining error message, not by key count.
            if (!deepObjHasTruthyNonObjValue(masterLevelErrors)) return true;

            // Do not populate the form with the validation errors
            if (silent) return false;

            if (mapper.parseForm) {
                setFormDeclarationErrors(mapper.parseForm(masterLevelErrors));
            }

            return false;
        },
        [mapper, masterFormik, runMasterFormValidation, setFormDeclarationErrors]
    );

    const updateProduct = async (form: any, declarationId: string) => {
        if (!products.product?.id || !mapper.updateProductFuncName) return;

        return (products[mapper.updateProductFuncName] as UpdateProductFunction)(
            form,
            declarationId,
            products.product?.id
        ).then((res) => {
            if (!res) return;
            setHasUnsavedChanges(false);
            return res;
        });
    };
    const createProduct = async (form: any, declarationId: string) => {
        if (products.product?.id || !mapper.createProductFuncName) return;

        if (declaration?.ieNctsDeclaration || declaration?.ukNctsDeclaration)
            form.houseConsignmentId = (masterFormik.values as NctsDeclaration)?.consignment?.houseConsignment?.[0]?.id;

        return (products[mapper.createProductFuncName] as CreateProductFunction)(form, declarationId).then((res) => {
            if (!res?.id) return;

            let data;

            if (mapper.transformProductCreateResponse) {
                const transformed = mapper.transformProductCreateResponse(res);
                data = mapper.transformData?.product?.forClient?.(transformed) ?? transformed ?? {};
            } else {
                data = mapper.transformData?.product?.forClient?.(res) ?? res ?? {};
            }

            productsFormik.setValues(data);
            setProductId(res.id);
            return data;
        });
    };
    /**
     * Resolves to whether the item was saved. A failed request does not throw: the product actions
     * report it with a notification and resolve with nothing, so the missing payload is the only
     * sign of it. Callers that move on afterwards have to stay when this is false.
     */
    const handleProductsFormSubmit = async (item: ProductsTypes): Promise<boolean> => {
        if (!declaration) return false;

        let form = removeEmptyObjectsFromDeclarationArrays(trimWhiteSpaces({ ...item }, { emptyStringToNull: true }));

        const isProductCreated = products.product?.id;

        if (mapper.transformData?.product?.forServer) {
            form = mapper.transformData.product.forServer(form);
        }

        // Both create and update send the item cleaned, the same way a declaration save does.
        form = removeEmptyObjects(form);

        let data;
        if (isProductCreated) {
            data = await updateProduct(form, declaration.id!);
        } else {
            data = await createProduct(form, declaration.id!);
        }

        if (!data) return false;

        window.EventBus.publish(Events.AFTER_DECLARATION_SAVE, data);

        await declarations.getDeclaration(declaration.id!);

        return true;
    };

    const saveDeclaration = async (data: any, declarationId: string, withNotification: boolean) => {
        const customerId = job?.customer?.id || (job?.customerId as string);

        try {
            const res = await (declarations[mapper.updateDeclarationFuncName] as UpdateDeclarationFunction)(
                customerId,
                declarationId,
                data,
                withNotification
            );

            window.EventBus.publish(Events.AFTER_DECLARATION_SAVE);
            setHasUnsavedChanges(false);
            setDeclarationSaved(true);

            return res;
        } catch (error) {
            Notification({
                type: 'error',
                messageTitle: 'Saving declaration failed.',
                description: getErrorMessage(error) || 'Something went wrong.',
                placement: 'top',
            });
        }
    };

    /**
     * The house consignment form edits one entry of `consignmentMasterLevel.consignmentHouseLevel`
     * in a formik of its own, so its values have to be folded back into the declaration before it is
     * saved. The stored `id` wins over whatever the form holds: it is what the backend matches the
     * entry on, and losing it would delete the house consignment and create a new one in its place.
     *
     * `goodsItem` is kept from the declaration for the same reason: the house consignment form does
     * not edit the items - they are saved through their own endpoints and listed back into the
     * payload before it is used - so the copy its values carry is the one it was loaded with, and
     * letting it win would drop every item created or changed since.
     */
    const withEditedHouseConsignment = (values: any) => {
        if (!consignmentId) return values;

        const consignmentMasterLevel = (values as ICS2EnsDeclarationPayload)?.consignmentMasterLevel;
        const houseConsignments = consignmentMasterLevel?.consignmentHouseLevel;

        if (!houseConsignments?.length) return values;

        return {
            ...values,
            consignmentMasterLevel: {
                ...consignmentMasterLevel,
                consignmentHouseLevel: houseConsignments.map((houseConsignment) =>
                    houseConsignment.id === consignmentId
                        ? {
                              ...houseConsignment,
                              ...houseConsignmentFormik.values,
                              id: houseConsignment.id,
                              goodsItem: houseConsignment.goodsItem,
                          }
                        : houseConsignment
                ),
            },
        };
    };

    const saveAsDraft = async (
        withNotification = true,
        paramData?: Partial<TDeclaration>,
        declarationWithNewItem?: TDeclaration
    ) => {
        if (!declaration) return;

        let form = removeEmptyObjects(
            removeEmptyObjectsFromDeclarationArrays(
                trimWhiteSpaces(
                    withEditedHouseConsignment(declarationWithNewItem ?? merge(masterFormik.values, paramData)),
                    { emptyStringToNull: true }
                )
            )
        );

        if (mapper.transformData?.declaration?.forServer !== undefined) {
            if (mapper.payloadPath) {
                form = {
                    ...declaration?.[mapper.declarationType],
                    [mapper.payloadPath]: form,
                };
            }

            form = mapper.transformData.declaration.forServer(form);
        }

        const formWithoutNotifications = omit(form, ['notifications']);
        const data = { ...declaration, [mapper.declarationType]: formWithoutNotifications };

        window.EventBus.publish(Events.BEFORE_DECLARATION_SAVE, true);

        await saveDeclaration(data, declaration.id!, withNotification);
    };

    const { setDUCRMUCR, previousDocumentPath } = useCDSPreviousDocumentUtils({ formik: masterFormik });

    const refetchDeclaration = useCallback(() => {
        return declarations.getDeclaration(declaration?.id!);
    }, [declaration?.id, declarations]);

    useDeclarationAutoRefresh();
    useUpdateViewOfDeclaration({
        declaration: declarations.declaration,
        declarationIsArchived: declarations.declaration?.archived,
    });

    useEffect(() => {
        clearFormDeclarationErrors();
        listExchanges();
        return () => {
            clearFormDeclarationErrors();
            products.clearError();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        masterFormik.setValues(
            (mapper.payloadPath ? get(currentDeclaration, mapper.payloadPath) : (currentDeclaration as any)) ?? {}
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDeclaration]);
    useEffect(() => {
        productsFormik.setValues(productInitialValues ?? {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productInitialValues]);

    // Trigger transform
    useEffect(() => {
        const transformForClient = mapper.transformData?.declaration?.forClient;

        if (transformForClient) {
            setDeclaration(transformForClient(declarations.declaration, false));
        } else {
            setDeclaration(declarations.declaration);
        }
    }, [mapper.transformData?.declaration?.forClient, declarations.declaration, mapper.transformData]);

    const { saveOpenProduct } = useHandleFormAction({
        productsFormik,
        saveDeclaration: saveAsDraft,
        saveProduct: handleProductsFormSubmit,
        validateForm,
        refetchDeclaration,
    });
    useAutoSaveProduct({
        setFormAction,
        declarationStatus: declaration?.status,
        productsFormik,
        fieldsToAutosaveOn: mapper.productFieldsToAutosaveOn,
    });
    useHandleErrors({
        formik: masterFormik,
        productsFormik,
        houseConsignmentFormik,
        validationErrorsParser: mapper.parseForm,
        houseConsignmentErrorsParser: mapper.parseHouseConsignmentForm,
        houseConsignmentIndex,
        triedToSubmit,
    });
    useHandleUnsavedChanges({
        declarationSaved,
        formik: masterFormik,
        productsFormik,
        houseConsignmentFormik,
        setDeclarationSaved,
    });
    // Set only on a declaration that has just been generated by mirroring, and only for as long as it
    // takes to offer the user the master level fields the mirroring could not derive.
    const { request: postMirrorCompletionRequest, clearRequest } = usePostMirrorCompletionRequest();

    const triggerSaveDraftFlow = useCallback(() => {
        showGlobalOverlay({ type: 'LoadingOverlay' });
        setTriedToSubmit(false);
        setFormAction(FormAction.DRAFT);
    }, [setFormAction, showGlobalOverlay]);

    // An item is open when the url names one. The footer type says nothing about it: it is only set
    // by the add buttons and the Items tab, so an item opened from the table or reloaded was skipped.
    const shouldSaveProductFirst = !!productId && !!urlParamsProductId;

    const isIcs2 = Boolean(declaration?.ics2ENSDeclaration);

    /**
     * Submitting an ICS2 declaration validates all four of its levels - the master level, the master
     * items, the house consignments and their items. They all live in the single payload the master
     * form holds, so what is saved is validated in one run over it, with the item and the house
     * consignment currently open folded in first and validated in their own forms as well, so that
     * whichever form the user is looking at highlights what is missing.
     */
    const triggerIcs2SubmitFlow = useCallback(async () => {
        if (!declaration?.id) return;

        const listProducts = mapper.listProductsFuncName
            ? (products[mapper.listProductsFuncName] as ListProductFunction)
            : undefined;

        if (!mapper.itemPaths || !listProducts) throw new Error('The declaration has no goods items mapped.');

        // The open item saves through its own endpoint, so that the items listed below already carry
        // its edits. Saving it refetches the declaration, so nothing else has to.
        if (productId) {
            const productSaved = await handleProductsFormSubmit(productsFormik.values);
            // The failure was reported, and submitting without the item would send the old one.
            if (!productSaved) return;
        }

        const values = await listItemsIntoForm({
            values: masterFormik.values,
            declarationId: declaration.id,
            itemPaths: mapper.itemPaths,
            listProducts,
        });

        await saveAsDraft(true, {}, values as TDeclaration);

        // The house consignment being edited is folded into the declaration by the save, so the
        // same values are what has to be validated.
        const isValid = await validateForm(withEditedHouseConsignment(values));

        // Whichever form is open reports its own level too, so its fields are marked right away
        // rather than only once the user steps back into it.
        if (consignmentId) {
            const houseConsignmentErrors = await houseConsignmentFormik.validateForm();

            houseConsignmentFormik.setTouched(setNestedObjectValues(houseConsignmentErrors, true));
        }
        if (productId) {
            const productErrors = await productsFormik.validateForm();

            productsFormik.setTouched(setNestedObjectValues(productErrors, true));
        }

        if (!isValid) showErrorNotification(t('error.declaration_invalid_title'), t('error.formRequired'));

        setTriedToSubmit(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        consignmentId,
        declaration?.id,
        handleProductsFormSubmit,
        houseConsignmentFormik,
        mapper.itemPaths,
        mapper.listProductsFuncName,
        masterFormik.values,
        productId,
        products,
        productsFormik,
        saveAsDraft,
        showErrorNotification,
        t,
        validateForm,
    ]);

    const triggerSubmitFlow = useCallback(async () => {
        showGlobalOverlay({ type: 'LoadingOverlay' });

        try {
            if (!declaration?.id) return;

            if (isIcs2) {
                await triggerIcs2SubmitFlow();
                return;
            }

            const params: Partial<PaginatedParams> = { size: 1000 };

            let declarationWitNewItem: TDeclaration | undefined = undefined;

            if (shouldSaveProductFirst && mapper.listProductsFuncName) {
                const productSaved = await handleProductsFormSubmit(productsFormik.values);
                // The failure was reported, and submitting without the item would send the old one.
                if (!productSaved) return;

                await refetchDeclaration();
                await validateForm();

                const itemPaths: ItemPathMappings =
                    mapper.itemPaths ?? (mapper.itemPath ? [{ path: mapper.itemPath, listParams: params }] : []);

                if (itemPaths.length) {
                    declarationWitNewItem = await listItemsIntoForm({
                        values: masterFormik.values,
                        declarationId: declaration.id,
                        itemPaths,
                        listProducts: products[mapper.listProductsFuncName] as ListProductFunction,
                    });
                }
            }

            // Save once before extra steps
            await saveAsDraft(false, {}, declarationWitNewItem);

            // The values that were just saved are validated, not the form: it only takes on the saved
            // declaration a render later, and validating before that misses the changes of the item.
            let valuesToValidate = declarationWitNewItem;

            /**
             * Handle additional CDS Export submission steps
             * - DUCR generation
             */
            if (
                declarations.declaration?.declarationInternalType === DeclarationInternalType.EXPORT &&
                declarations.declaration?.declarationExternalEntity === DeclarationExternalEntity.CDS
            ) {
                try {
                    const generatedDucr = await generateDucr(declaration.id);
                    if (generatedDucr !== (masterFormik?.values as CdsDeclarationPayloadPayload)?.ducr) {
                        const newPreviousDocuments = setDUCRMUCR('DCR', generatedDucr);
                        await saveAsDraft(false, set({}, previousDocumentPath, newPreviousDocuments));
                        // The generated DUCR is only in the form, not in the values that were saved first.
                        valuesToValidate = undefined;
                    }
                } catch {
                    notification.error({
                        message: '"Exporter - Identification Number" is required for DUCR generation',
                    });
                }
            }

            await validateForm(valuesToValidate);

            setTriedToSubmit(true);
        } catch (err) {
            Notification({
                type: 'error',
                messageTitle: 'Could not update declaration',
                description: getErrorMessage(err) || 'Something went wrong. Please try again.',
                placement: 'top',
            });
        } finally {
            hideGlobalOverlay();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        declarations.declaration?.declarationExternalEntity,
        declarations.declaration?.declarationInternalType,
        declaration?.id,
        isIcs2,
        triggerIcs2SubmitFlow,
        masterFormik?.values,
        generateDucr,
        handleProductsFormSubmit,
        hideGlobalOverlay,
        mapper.itemPath,
        mapper.itemPaths,
        mapper.listProductsFuncName,
        notification,
        previousDocumentPath,
        products,
        productsFormik.values,
        refetchDeclaration,
        saveAsDraft,
        setDUCRMUCR,
        setTriedToSubmit,
        showErrorNotification,
        showGlobalOverlay,
        t,
        validateForm,
    ]);

    const triggerSaveProduct = useCallback(() => {
        showGlobalOverlay({
            type: 'LoadingOverlay',
        });
        setTriedToSubmit(false);
        setFormAction(FormAction.PRODUCT);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The revalidation runs after the declaration was refetched, so it has to call the validation
    // of the render that follows rather than the one it was started from.
    const validateFormRef = useRef(validateForm);
    validateFormRef.current = validateForm;

    /**
     * Items that were added, deleted or moved change which item every listed error belongs to, so
     * when errors are showing they are worked out again from the refetched declaration.
     */
    const revalidateItems = async () => {
        if (!hasFormDeclarationErrors) return;

        await refetchDeclaration();
        // Let the form reinitialise from the refetched declaration before it is validated.
        await new Promise((resolve) => setTimeout(resolve, 0));
        await validateFormRef.current();
    };

    /**
     * Saves the open item before the form is reset or left, and waits for it. The values are
     * compared with the initial ones rather than relying on touched fields or the unsaved changes
     * flag: edits made through buttons (Box44, rows of a card) never fire a blur. Resolves to
     * false when the save failed, so the caller stays on the item.
     */
    const saveOpenProductIfNeeded = async (): Promise<boolean> => {
        const isDeclarationEditable =
            declaration?.status === DeclarationStatuses.DRAFT ||
            declaration?.status === DeclarationStatuses.REJECTED ||
            declaration?.status === DeclarationStatuses.INVALID;
        const hasUnsavedProduct = !isEqual(productsFormik.values, productsFormik.initialValues);

        const needsSave = !!productId && isDeclarationEditable && !inViewOnly && hasUnsavedProduct;
        if (!needsSave) return true;

        const { saved } = await saveOpenProduct();
        return saved;
    };

    const triggerCancelSaveProduct = useCallback(async () => {
        const saved = await saveOpenProductIfNeeded();
        if (!saved) return;

        setCancelClicked(true);
        navigate(`${baseUrl}${inViewOnly ? '/view-only' : ''}/products`, {
            replace: true,
            state: { comingBackFromProductView: true },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        baseUrl,
        inViewOnly,
        navigate,
        productId,
        productsFormik.values,
        productsFormik.initialValues,
        declaration?.status,
        saveOpenProduct,
    ]);

    // Subscribe to events
    useEffect(() => {
        const triggerSubmitSubscriberId = window.EventBus.subscribe(Events.TRIGGER_SUBMIT_DECLARATION, () => {
            triggerSubmitFlow();
        });

        const triggerSaveAsDraftSubscriberId = window.EventBus.subscribe(
            Events.TRIGGER_SAVE_DECLARATION_AS_DRAFT,
            () => {
                triggerSaveDraftFlow();
            }
        );

        const triggerSaveProductSubscriberId = window.EventBus.subscribe(Events.TRIGGER_SAVE_PRODUCT, () => {
            triggerSaveProduct();
        });

        const triggerCancelSaveProductSubscriberId = window.EventBus.subscribe(
            Events.TRIGGER_CANCEL_SAVE_PRODUCT,
            () => {
                triggerCancelSaveProduct();
            }
        );

        const triggerValidateMasterFormSubscriberId = window.EventBus.subscribe(
            Events.TRIGGER_VALIDATE_MASTER_FORM,
            async (payload?: { silent?: boolean }) => {
                const isValid = await validateMasterLevelForm({ silent: payload?.silent });
                window.EventBus.publish(Events.MASTER_FORM_VALIDATION_END, isValid);
            }
        );

        return () => {
            window.EventBus.unsubscribe(triggerSubmitSubscriberId);
            window.EventBus.unsubscribe(triggerSaveAsDraftSubscriberId);
            window.EventBus.unsubscribe(triggerSaveProductSubscriberId);
            window.EventBus.unsubscribe(triggerCancelSaveProductSubscriberId);
            window.EventBus.unsubscribe(triggerValidateMasterFormSubscriberId);
        };
    }, [
        triggerSubmitFlow,
        triggerSaveDraftFlow,
        triggerSaveProduct,
        triggerCancelSaveProduct,
        validateMasterLevelForm,
    ]);

    return (
        <DeclarationContextProvider>
            <TemplateContextProvider>
                <DeclarationSourceContextProvider payloadPath={mapper.payloadPath}>
                    <ProductContextProvider productId={productId} setProductId={(value) => setProductId(value)}>
                        <StyledHeader>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <DeclarationPopupManager
                                    submitting={triedToSubmit}
                                    submissionModalsActions={{
                                        validateForm,
                                        onCancel: () => setTriedToSubmit(false),
                                    }}
                                    formik={masterFormik}
                                    saveAsDraft={saveAsDraft}
                                />
                            </div>
                        </StyledHeader>
                        {declarations?.declaration?.gvmsDeclaration &&
                            declarations?.declaration.status === DeclarationStatuses.DRAFT && (
                                <GvmsHeaderDraftMessage />
                            )}
                        {postMirrorCompletionRequest && (
                            <PostMirrorCompletionPrompt
                                request={postMirrorCompletionRequest}
                                formik={masterFormik}
                                isDeclarationLoaded={Boolean(declaration?.id && declaration.id === declarationId)}
                                saveDeclaration={() => saveAsDraft()}
                                onDone={clearRequest}
                            />
                        )}
                        <StyledLayout>
                            <Outlet
                                context={{
                                    formik: masterFormik,
                                    productsFormik,
                                    houseConsignmentFormik,
                                    cancelProducts: cancelClicked,
                                    clearCancel: () => {
                                        setCancelClicked(false);
                                    },
                                    saveAsDraft,
                                    saveOpenProductIfNeeded,
                                    revalidateItems,
                                    declaration,
                                }}
                            />
                        </StyledLayout>
                    </ProductContextProvider>
                </DeclarationSourceContextProvider>
            </TemplateContextProvider>
        </DeclarationContextProvider>
    );
};

export default DeclarationFormTab;
